import { Verified as VerifiedIcon } from '@material-ui/icons';
import React, { useEffect, useRef } from 'react';
import Card from '../../../common/card/Card';
import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineHtml from '../../../common/inline/InlineHtml';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { useUpdateChangeOrder } from '../ChangeOrder.hook';
import {
  BaseChangeOrderCardProps,
  ChangeOrder
} from '../ChangeOrder.interface';

const ChangeOrderVerificationOfChangeRecordsCardFields = [
  {
    name: 'verifyEffectiveness',
    label: 'Verify effectiveness',
    description: '(e.g., successful V&V, no unintended issues)'
  },
  {
    name: 'changeRecordsAndDocumentationUpdated',
    label: 'Change records and documentation updated',
    description:
      'e.g.Updated and approved changes/ new Reqs & Specs, V&V test plans, DHF, DMR, if applicable'
  }
];
export interface ChangeOrderVerificationOfChangeRecordsCardProps
  extends BaseChangeOrderCardProps {
  changeOrder: ChangeOrder;
  setSuccessMessage: (message: string) => void;
  setErrorMessage: (message: string) => void;
}

export const ChangeOrderVerificationOfChangeRecordsCard: React.FC<
  ChangeOrderVerificationOfChangeRecordsCardProps
> = props => {
  const { changeOrder, onChange, setSuccessMessage, setErrorMessage } = props;
  const onChangeRef = useRef(onChange);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(changeOrder.task.id || 0);
  const { updatedChangeOrder, isLoading, error, updateChangeOrder } =
    useUpdateChangeOrder(changeOrder.id);
  const { isFieldDisabled } = useTaskEntityFields(changeOrder.task);

  const initialValues: Partial<ChangeOrder> = {
    verifyEffectiveness: changeOrder.verifyEffectiveness || '',
    changeRecordsAndDocumentationUpdated:
      changeOrder.changeRecordsAndDocumentationUpdated || ''
  };
  useEffect(() => {
    if (updatedChangeOrder?.id) {
      setSuccessMessage('Change Order successfully updated');
      onChangeRef.current?.(updatedChangeOrder);
    }
  }, [setSuccessMessage, updatedChangeOrder]);

  useEffect(() => {
    if (updatedTask) {
      changeOrder.task = Object.assign(changeOrder.task || {}, updatedTask);
      onChange?.(changeOrder);
    }
  }, [changeOrder, onChange, updatedTask]);

  useEffect(() => {
    if (Boolean(error || updateTaskError))
      setErrorMessage('There was an error saving');
  }, [setErrorMessage, error, updateTaskError]);

  return (
    <InlineFormik
      initialValues={initialValues}
      strict={false}
      isSubmitting={isLoading}
      error={error}
      onSubmit={(updatedValue: Partial<ChangeOrder>) => {
        const dto = { ...updatedValue };
        updateChangeOrder(dto);
      }}
    >
      <Card>
        <CardTitle>
          <VerifiedIcon />
          Verification of Change Records
        </CardTitle>
        <CardContent padding='large'>
          <InlineForm>
            {ChangeOrderVerificationOfChangeRecordsCardFields.map(
              (field, index) => (
                <InlineHtml
                  key={index}
                  name={field.name}
                  label={field.label}
                  disabled={isFieldDisabled(field.name)}
                  helperText={field.description}
                  attachments={changeOrder.task.attachments || []}
                  onChangeAttachments={attachments => {
                    updateTask({ attachments });
                    if (changeOrder.task) {
                      changeOrder.task.attachments = attachments;
                    }
                    onChange?.(changeOrder);
                  }}
                />
              )
            )}
          </InlineForm>
        </CardContent>
      </Card>
    </InlineFormik>
  );
};
